/**
 * Round 53: the web's tournament cards on Discover — the web has no banner,
 * so this is where a member sees what the app's banner shows: every tournament
 * that is announced ("Notify me"), open for sign-up ("Sign up") or being
 * played ("See the bracket"). Reads the same list as the app
 * (GET /api/tournaments); renders nothing when there is none.
 * Entries are Emeralds; rewards are a share of the entries, paid in Rubies.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const api = axios.create({ baseURL: API_BASE_URL, withCredentials: true });
api.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch (e) { /* private window */ }
  return config;
});

const num = (v) => Number(v || 0).toLocaleString();
const STATUS = { registration: 'Sign-up open', closed: 'Sign-up closed', checkin: 'Check-in', in_progress: 'Tonight' };

const TournamentCards = () => {
  const [items, setItems] = useState(null);
  const [notified, setNotified] = useState({});
  const [busy, setBusy] = useState('');

  const load = async () => {
    try {
      const r = await api.get('/api/tournaments');
      const list = Array.isArray(r.data?.tournaments) ? r.data.tournaments : [];
      setItems(list.filter((t) => t && t.id && (t.announced || !t.coming_soon)));
    } catch (e) { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const notifyMe = async (t) => {
    if (busy) return;
    setBusy(t.id);
    try {
      await api.post(`/api/tournaments/${encodeURIComponent(t.slug || t.id)}/notify-me`);
      setNotified((n) => ({ ...n, [t.id]: true }));
    } catch (e) {
      if (e?.response?.status === 401) window.location.href = '/app';
    } finally { setBusy(''); }
  };

  if (!items || items.length === 0) return null;

  return (
    <div className="mb-12" data-testid="discover-tournaments">
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--gems-text)' }}>
        <span className="text-2xl">🏆</span> Tournaments
      </h2>
      <div className="grid md:grid-cols-3 gap-4">
        {items.map((t) => {
          const c = t.colors || { accent: '#3EE6A4', ink: '#062015', top: '#0d3b27' };
          const announced = t.announced && t.status === 'draft';
          const open = t.status === 'registration';
          const night = ['closed', 'checkin', 'in_progress'].includes(t.status);
          const bracketOut = night && !!t.started_at;
          const page = `/t/${encodeURIComponent(t.slug || t.id)}`;
          const rewards = t.rewards?.mode === 'share'
            ? `${t.rewards.rewards_pct ?? 90}% of all entries come back as rewards`
            : 'fixed Ruby rewards for the top teams';
          const chip = announced ? 'Opens soon' : bracketOut ? 'Bracket is out' : STATUS[t.status] || '';
          return (
            <div key={t.id} className="rounded-xl border-2 p-4 flex flex-col" style={{ borderColor: c.accent, background: `linear-gradient(135deg, ${c.accent}22 0%, ${c.top || '#000'}66 100%)` }} data-testid={`discover-tournament-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: c.accent }}>{[t.occasion, t.level_name && `${t.level_name} level`].filter(Boolean).join(' · ') || 'Spades · teams of two'}</p>
                  <h3 className="font-bold text-lg leading-tight mt-0.5" style={{ color: 'var(--gems-text)' }}>{t.name}</h3>
                </div>
                {chip && <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: c.accent, color: c.ink }}>{chip.toUpperCase()}</span>}
              </div>
              <p className="text-sm mt-2 font-semibold" style={{ color: c.accent }}>{t.starts_label}</p>
              <p className="text-xs mt-1 text-gray-300">
                {t.entry ? `Entry ${num(t.entry)} Emeralds per player` : 'Free entry'} · {rewards} · {t.counts?.confirmed_teams ?? 0} of {t.max_teams} teams
              </p>
              <div className="mt-auto pt-3">
                {announced && (notified[t.id] || t.me?.notify_me
                  ? <p className="text-sm font-semibold text-center" style={{ color: c.accent }}>✓ You're on the list — we'll tell you when sign-up opens</p>
                  : <button onClick={() => notifyMe(t)} disabled={busy === t.id} className="w-full text-sm font-bold px-3 py-2 rounded-full" style={{ background: c.accent, color: c.ink, opacity: busy === t.id ? 0.6 : 1 }} data-testid={`tournament-notify-${t.id}`}>Notify me</button>)}
                {open && <a href={page} className="block w-full text-center text-sm font-bold px-3 py-2 rounded-full" style={{ background: c.accent, color: c.ink }} data-testid={`tournament-signup-${t.id}`}>Sign up</a>}
                {night && <a href={page} className="block w-full text-center text-sm font-bold px-3 py-2 rounded-full" style={{ background: c.accent, color: c.ink }}>{bracketOut ? 'See the bracket' : 'Open'}</a>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TournamentCards;
